import { useRef, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Loader2, X, RefreshCcw, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { resolveToolConfig, STATUS_BAR_TOOLS } from './toolUtils';
import { theme } from '../../styles/theme';
import { AGENT_EVENT_TYPES } from '../../constants';
import { ConnieAvatar } from '../../components/ConnieAvatar';
import { ToolStatusBar } from './ToolStatusBar';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { AgentEvent } from '../../types';

const WORKING_MESSAGES = [
  'Crafting your perfect getaway…',
  'Searching the best options…',
  'Consulting my secret maps…',
  'Comparing destinations for you…',
  'Crunching the numbers…',
  'Putting it all together…',
  'Polishing your itinerary…',
  'Adding the finishing touches…',
];

function useRotatingText(items: string[], intervalMs: number) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex(i => (i + 1) % items.length), intervalMs);
    return () => clearInterval(id);
  }, [items, intervalMs]);
  return items[index];
}

function useElapsedTimer(running: boolean) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!running) return;
    setSeconds(0);
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

const STATUS_BAR_KEYS = new Set(STATUS_BAR_TOOLS.map(t => t.key));

interface GeneratingScreenProps {
  destination: string;
  agentEvents: AgentEvent[];
  isModal?: boolean;
  onClose?: () => void;
  hasError?: boolean;
  onRetry?: () => void;
  onModifyTrip?: () => void;
}

export function GeneratingScreen({ destination, agentEvents, isModal, onClose, hasError, onRetry, onModifyTrip }: GeneratingScreenProps) {
  const mobile = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);
  const avatarSize = mobile ? 80 : 120;
  const ringSize = mobile ? 110 : 160;

  const hasStarted = !isModal && agentEvents.some(e => e.eventType === AGENT_EVENT_TYPES.AGENT_START);
  const elapsed = useElapsedTimer(hasStarted && !hasError);
  const statusMsg = useRotatingText(WORKING_MESSAGES, 4000);

  const history = useMemo(() => {
    if (!agentEvents.length) {
      return [{ type: 'message' as const, content: `Starting to plan your trip to ${destination}...` }];
    }
    return agentEvents.map(evt => {
      if (evt.eventType === AGENT_EVENT_TYPES.THINKING) {
        return { type: 'thinking' as const, content: evt.message };
      }
      const cfg = resolveToolConfig(evt.toolName, evt.eventType);
      if (cfg && STATUS_BAR_KEYS.has(cfg.key)) {
        return { type: 'tool' as const, content: evt.message, id: cfg.displayLabel, Icon: cfg.Icon };
      }
      return { type: 'message' as const, content: evt.message };
    });
  }, [agentEvents, destination]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history]);

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '1.5rem', textAlign: 'center', padding: isModal ? (mobile ? '1.5rem 1rem' : '3rem') : '1.5rem 1rem' }}>
      {/* Avatar */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.sm }}>
        <div style={{ position: 'relative' }}>
          {isModal ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
              style={{ width: ringSize, height: ringSize, border: `1px dashed ${theme.colors.tealBorder}`, borderRadius: '50%' }}
            />
          ) : hasError ? (
            <motion.div
              animate={{ boxShadow: [`0 0 0 0px rgba(212,160,23,0)`, `0 0 20px 8px rgba(212,160,23,0.2)`, `0 0 0 0px rgba(212,160,23,0)`] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ width: ringSize, height: ringSize, border: `2px dashed ${theme.colors.connieGold}`, borderRadius: '50%' }}
            />
          ) : (
            <motion.div
              animate={{ boxShadow: [`0 0 0 0px ${theme.colors.tealBorder}`, `0 0 20px 8px ${theme.colors.tealBorder}`, `0 0 0 0px ${theme.colors.tealBorder}`] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ width: ringSize, height: ringSize, borderRadius: '50%' }}
            />
          )}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div
              animate={hasError
                ? { scale: [1, 1.04, 1] }
                : { scale: [1, 1.05, 1], rotate: [0, 5, -5, 0] }
              }
              transition={{ duration: hasError ? 3 : 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ConnieAvatar size={avatarSize} style={{
                boxShadow: theme.shadows.xl,
                border: `1px solid ${theme.colors.tealBorder}`,
                padding: mobile ? 8 : 16,
              }} />
            </motion.div>
          </div>
        </div>

        {!isModal && hasStarted && !hasError && (
          <span style={{ fontFamily: theme.fontFamily.base, fontSize: theme.fontSize.xs, color: theme.colors.muted, fontVariantNumeric: 'tabular-nums' }}>
            {elapsed}
          </span>
        )}

        {!isModal && !hasError && (
          <AnimatePresence mode="wait">
            <motion.p
              key={statusMsg}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3 }}
              style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal, fontSize: theme.fontSize.base, margin: 0 }}
            >
              {statusMsg}
            </motion.p>
          </AnimatePresence>
        )}

        {!isModal && hasError && (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.connieGoldText, fontSize: theme.fontSize.base, margin: 0 }}
          >
            Oops, a hiccup!
          </motion.p>
        )}
      </div>

      {/* Text */}
      <div style={{ maxWidth: '40rem' }}>
        <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: mobile ? theme.fontSize.xl : theme.fontSize['2xl'], fontWeight: theme.fontWeight.light, color: theme.colors.navy, marginBottom: theme.spacing.md }}>
          {isModal ? (
            <>
              "Here's my <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>reasoning...</span>"
            </>
          ) : hasError ? (
            <>
              "My map blew away in the <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>wind!</span>"
            </>
          ) : (
            <>
              "I'm weaving your <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>magic itinerary...</span>"
            </>
          )}
        </h2>
        <p style={{ color: theme.colors.muted, fontWeight: theme.fontWeight.light, maxWidth: '24rem', margin: '0 auto' }}>
          {isModal ? (
            `How I curated your bespoke journey to ${destination}.`
          ) : hasError ? (
            "Something didn't go as planned. Check the reasoning below for details — then try again or modify your trip."
          ) : (
            <>Consulting my secret maps... and our <a href="https://www.automat-it.com/solutions/llm-selection-optimizer/" target="_blank" rel="noopener noreferrer" style={{ fontStyle: 'italic', color: theme.colors.teal, textDecoration: 'none' }}>LLM Selection Optimizer</a> is picking the most efficient model to build your dream.</>
          )}
        </p>
      </div>

      {/* Error action buttons */}
      {hasError && !isModal && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          style={{ display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          {onModifyTrip && (
            <button className="btn-secondary" onClick={onModifyTrip}>
              <RotateCcw size={16} /> Modify Trip
            </button>
          )}
          {onRetry && (
            <button
              className="btn-primary"
              onClick={onRetry}
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                fontSize: '10px',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.15em',
              }}
            >
              <RefreshCcw size={14} /> Try Again
            </button>
          )}
        </motion.div>
      )}

      {/* Tool status bar — hidden on error */}
      {!hasError && (
        <ToolStatusBar events={agentEvents} />
      )}

      {/* Chain of thought — only reasoning on error */}
      <div style={{ width: '100%', maxWidth: '40rem', flex: 1, minHeight: '300px', maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
        <div ref={scrollRef} style={{
          flex: 1, height: 0,
          overflowY: 'scroll', padding: `0 ${theme.spacing.md}`,
          display: 'flex', flexDirection: 'column', gap: theme.spacing.md,
          overflowAnchor: 'none', textAlign: 'left',
        }}>
          {(hasError ? history.filter(item => item.type === 'thinking') : history).map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.sm, ...(item.type === 'tool' && !mobile ? { paddingLeft: theme.spacing['2xl'] } : {}) }}>
              <div style={{
                marginTop: 4, flexShrink: 0, width: mobile ? 24 : 32, height: mobile ? 24 : 32, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: item.type === 'tool' ? theme.colors.tealBg : item.type === 'thinking' ? '#dbeafe' : `${theme.colors.navy}0d`,
                color: item.type === 'tool' ? theme.colors.teal : item.type === 'thinking' ? '#3b82f6' : theme.colors.navy,
              }}>
                {item.type === 'tool'
                  ? (() => { const Icon = (item as any).Icon; return <Icon size={mobile ? 12 : 16} />; })()
                  : <ConnieAvatar size={mobile ? 14 : 20} style={{ border: 'none', padding: 0 }} />}
              </div>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                width: item.type === 'thinking' ? '100%' : undefined,
                maxWidth: item.type === 'thinking' ? undefined : '80%',
                padding: theme.spacing.md, borderRadius: theme.borderRadius.lg,
                background: item.type === 'tool' ? theme.colors.tealBg : item.type === 'thinking' ? '#f8fcff' : 'white',
                border: `1px solid ${item.type === 'tool' ? theme.colors.tealBorder : item.type === 'thinking' ? '#e8f4fd' : theme.colors.borderLight}`,
                boxShadow: theme.shadows.card,
              }}>
                {item.type === 'tool' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs, marginBottom: 2 }}>
                    <span style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.15em', color: theme.colors.teal }}>
                      {'id' in item ? item.id : 'Agent'}
                    </span>
                    {i === history.length - 1 && <Loader2 size={12} style={{ animation: 'pulse 1.5s infinite' }} color={theme.colors.teal} />}
                  </div>
                )}
                {item.type === 'thinking' && (
                  <div style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#93c5fd', marginBottom: 2 }}>
                    Thinking
                  </div>
                )}
                {item.type === 'thinking' ? (
                  <div style={{
                    fontSize: theme.fontSize.base, lineHeight: 1.6,
                    color: theme.colors.navy, fontWeight: theme.fontWeight.light,
                    fontStyle: 'italic', textAlign: 'left',
                  }}>
                    <ReactMarkdown>{item.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p style={{
                    fontSize: theme.fontSize.sm, lineHeight: 1.6,
                    color: item.type === 'tool' ? theme.colors.navy : theme.colors.textMuted,
                    fontWeight: item.type === 'tool' ? theme.fontWeight.medium : theme.fontWeight.light,
                    fontStyle: item.type === 'tool' ? 'italic' : 'normal',
                    margin: 0,
                  }}>
                    {item.content}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: mobile ? '1rem' : '2rem', background: 'rgba(30,41,59,0.4)',
            backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          }}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', width: '100%', maxWidth: '56rem',
              borderRadius: theme.borderRadius['3xl'], boxShadow: theme.shadows.xl,
              overflow: 'hidden', position: 'relative', maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: '2rem', right: '2rem', zIndex: 10,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: theme.spacing.sm, borderRadius: '50%',
              }}
            >
              <X size={24} color={theme.colors.muted} />
            </button>
            {content}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return content;
}
